/* Header product menus: native details, keyboard navigation and scroll-snap rails. */
(() => {
  const desktopQuery = '(min-width: 1024px)';
  const finePointerQuery = '(hover: hover) and (pointer: fine)';

  const headerStates = new WeakMap();

  if (!customElements.get('nosht-product-menu')) {
    class NoshtProductMenu extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;
        this.details = this.querySelector('[data-menu-details]');
        this.trigger = this.querySelector('[data-menu-trigger]');
        this.toggle = this.querySelector('[data-menu-toggle]');
        this.panel = this.querySelector('[data-menu-panel]');
        this.content = this.panel?.querySelector('nosht-menu-carousel');
        this.header = this.closest('#MainHeader');
        if (!this.details || !this.trigger || !this.toggle || !this.panel || !this.header) return;
        if (!headerStates.has(this.header)) headerStates.set(this.header, { active: null, target: null, version: 0, animation: null });
        this.state = headerStates.get(this.header);
        this.controller = new AbortController();
        const { signal } = this.controller;
        this.desktop = window.matchMedia(desktopQuery);
        this.finePointer = window.matchMedia(finePointerQuery);
        this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.details.addEventListener('toggle', () => {
          if (this.details.open && this.state.active !== this) this.open();
          else if (!this.details.open && this.state.active === this) this.close();
          else this.syncState();
        }, { signal });
        // The title stays a native link. Only the separate disclosure toggles the panel.
        this.toggle.addEventListener('click', event => {
          event.preventDefault();
          if (this.state.target === this) this.close();
          else this.open();
        }, { signal });
        this.trigger.addEventListener('pointerenter', () => {
          if (this.desktop.matches && this.finePointer.matches) this.open();
        }, { signal });
        this.addEventListener('pointerenter', () => this.cancelClose(), { signal });
        this.addEventListener('pointerleave', () => this.scheduleClose(), { signal });
        this.addEventListener('focusin', () => this.cancelClose(), { signal });
        this.addEventListener('focusout', event => {
          // Native focus can briefly be BODY between a product and an arrow button.
          if (event.relatedTarget && this.contains(event.relatedTarget)) return;
          queueMicrotask(() => {
            if (this.isConnected && this.state.target === this && !this.contains(document.activeElement) && !this.pinned) this.close();
          });
        }, { signal });
        this.addEventListener('keydown', event => this.onKeydown(event), { signal });
        this.querySelector('[data-menu-overlay]')?.addEventListener('click', () => this.close(), { signal });
        document.addEventListener('pointerdown', event => {
          if ((this.details.open || this.state.target === this) && !this.contains(event.target) && !this.pinned) this.close();
        }, { signal, capture: true });
        document.addEventListener('scroll', event => {
          const pageScroll = event.target === document || event.target === document.documentElement || event.target === document.body;
          if (pageScroll && (this.details.open || this.state.target === this) && !this.pinned) this.close();
        }, { signal, capture: true, passive: true });
        window.addEventListener('resize', () => {
          if (!this.desktop.matches) this.close();
          this.measure();
        }, { signal, passive: true });
        this.header.addEventListener('click', event => {
          if (!this.contains(event.target)) this.close();
        }, { signal });
        this.header.addEventListener('toggle', event => {
          if (event.target.matches?.('header-dropdown details') && event.target.open) {
            this.pinned = false;
            this.close();
          }
        }, { signal, capture: true });
        if ('ResizeObserver' in window) {
          this.observer = new ResizeObserver(() => this.measure());
          this.observer.observe(this.header);
          this.observer.observe(this.panel);
        }
        this.syncState();
      }

      disconnectedCallback() {
        this.close();
        this.observer?.disconnect();
        this.controller?.abort();
        this.controller = null;
        this.pinned = false;
      }

      measure() {
        if (!this.header || !this.panel) return;
        const available = Math.max(0, window.innerHeight - this.header.getBoundingClientRect().bottom - 12);
        this.panel.style.setProperty('--nosht-menu-available-height', `${available}px`);
        const height = `${this.panel.getBoundingClientRect().height}px`;
        this.details.style.setProperty('--nosht-menu-panel-height', height);
        // Closed siblings must not reset the visible menu's shared glass surface.
        if (this.state?.active === this && this.details.open) {
          this.header.style.setProperty('--nosht-header-menu-height', height);
        }
      }

      open() {
        this.cancelClose();
        if (this.state?.target === this) return this.opening || Promise.resolve(this.details.open);
        this.opening = this.performOpen();
        return this.opening;
      }

      async performOpen() {
        if (!this.state || !this.isConnected) return false;
        const state = this.state;
        const previous = state.active;
        const switching = Boolean(previous && previous !== this && previous.details.open);
        const returning = previous === this && this.details.open;
        const previousOpacity = previous?.content ? getComputedStyle(previous.content).opacity : '1';
        state.target = this;
        const version = ++state.version;
        state.animation?.cancel();
        state.animation = null;
        if (state.active !== this) this.details.open = false;
        this.updateHeader();
        // Reversing a pending A→B switch restores A without replaying its entrance.
        if (returning) {
          this.syncState();
          if (!this.motion.matches && this.content?.animate && Number(previousOpacity) < 1) {
            const animation = this.content.animate([{ opacity: previousOpacity }, { opacity: 1 }], { duration: 140, easing: 'ease-out' });
            state.animation = animation;
            await animation.finished.catch(() => {});
            if (state.version !== version) return false;
            state.animation = null;
          }
          return this.details.open;
        }
        if (switching) {
          previous.cancelClose();
          previous.pinned = false;
          previous.syncState();
          if (!this.motion.matches && previous.content?.animate) {
            const animation = previous.content.animate([{ opacity: previousOpacity }, { opacity: 0 }], { duration: 100, easing: 'ease-out', fill: 'forwards' });
            state.animation = animation;
            await animation.finished.catch(() => {});
            if (state.version !== version || !this.isConnected) return false;
            state.active = null;
            previous.finishClose();
            animation.cancel();
            state.animation = null;
          } else {
            state.active = null;
            previous.finishClose();
          }
        }
        if (state.version !== version || !this.isConnected) return false;
        this.closeLegacyMenus();
        state.active = this;
        this.details.open = true;
        this.syncState();
        this.measure();
        this.dispatchEvent(new CustomEvent('nosht:menu-open', { bubbles: true }));
        this.querySelectorAll('nosht-menu-carousel').forEach(rail => rail.refresh?.());
        if (!this.motion.matches && this.panel.animate) {
          const target = switching ? this.content : this.panel;
          const frames = switching
            ? [{ opacity: 0 }, { opacity: 1 }]
            : [{ opacity: 0, transform: 'translateY(-12px)', clipPath: 'inset(0 0 100% 0)' }, { opacity: 1, transform: 'translateY(0)', clipPath: 'inset(0 0 0 0)' }];
          const animation = target.animate(frames, { duration: switching ? 140 : 220, easing: 'cubic-bezier(.2,.7,.2,1)' });
          state.animation = animation;
          await animation.finished.catch(() => {});
          if (state.version !== version) return false;
          state.animation = null;
        }
        return this.details.open;
      }

      closeLegacyMenus() {
        this.header.querySelectorAll('header-dropdown details[open]').forEach(details => {
          details.open = false;
          details.closest('header-dropdown')?.clearCloseTimer?.();
        });
        this.header.classList.remove('sub-menu-open');
      }

      finishClose() {
        this.cancelClose();
        if (!this.details) return;
        this.details.open = false;
        this.syncState();
      }

      close({ restoreFocus = false } = {}) {
        if (!this.state) return;
        const state = this.state;
        if (state.active === this || state.target === this) {
          const previous = state.active;
          const pending = state.target;
          ++state.version;
          state.animation?.cancel();
          state.animation = null;
          state.active = null;
          state.target = null;
          previous?.finishClose();
          if (pending !== previous) pending?.finishClose();
        } else this.finishClose();
        if (restoreFocus && this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
      }

      syncState() {
        if (!this.details) return;
        const open = this.details.open && this.state?.target === this;
        this.trigger.setAttribute('aria-expanded', String(open));
        this.toggle.setAttribute('aria-expanded', String(open));
        this.panel.inert = !open;
        this.panel.setAttribute('aria-hidden', String(!open));
        this.updateHeader();
      }

      updateHeader() {
        if (!this.header) return;
        const open = Boolean(this.state?.target || this.header.querySelector('nosht-product-menu details[open]'));
        this.header.classList.toggle('nosht-product-menu-open', open);
        if (!open) this.header.style.removeProperty('--nosht-header-menu-height');
      }

      cancelClose() {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }

      scheduleClose() {
        this.cancelClose();
        if (this.state?.target !== this || this.pinned || this.contains(document.activeElement)) return;
        this.closeTimer = window.setTimeout(() => this.close(), 220);
      }

      async onKeydown(event) {
        if (event.key === 'Escape' && (this.details.open || this.state.target === this)) {
          event.preventDefault();
          event.stopPropagation();
          this.close({ restoreFocus: true });
        } else if (event.target === this.trigger && (event.key === 'ArrowDown' || event.key === ' ')) {
          event.preventDefault();
          const opened = await this.open();
          if (opened && this.state.target === this) this.panel.querySelector('a[href], button:not([disabled]):not([hidden])')?.focus({ preventScroll: true });
        }
      }
    }
    customElements.define('nosht-product-menu', NoshtProductMenu);
  }

  if (!customElements.get('nosht-menu-carousel')) {
    class NoshtMenuCarousel extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;
        this.track = this.querySelector('[data-menu-track]');
        this.previous = this.querySelector('[data-menu-prev]');
        this.next = this.querySelector('[data-menu-next]');
        if (!this.track) return;
        this.controller = new AbortController();
        const { signal } = this.controller;
        this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.desktop = window.matchMedia(desktopQuery);
        this.track.addEventListener('scroll', () => this.scheduleRefresh(), { signal, passive: true });
        this.track.addEventListener('focusin', (event) => this.revealFocusedProduct(event), { signal });
        this.track.addEventListener('keydown', (event) => this.onTrackKeydown(event), { signal });
        this.previous?.addEventListener('click', () => this.advance(-1), { signal });
        this.next?.addEventListener('click', () => this.advance(1), { signal });
        window.addEventListener('resize', () => this.scheduleRefresh(), { signal, passive: true });
        this.addEventListener('load', () => this.scheduleRefresh(), { signal, capture: true });
        document.addEventListener('shopify:block:select', (event) => this.onEditorSelect(event), { signal });
        document.addEventListener('shopify:block:deselect', (event) => this.onEditorDeselect(event), { signal });

        if ('ResizeObserver' in window) {
          this.observer = new ResizeObserver(() => this.scheduleRefresh());
          this.observer.observe(this.track);
          this.observer.observe(this);
        }
        this.drawer = this.closest('drawer-menu');
        if (this.drawer && 'MutationObserver' in window) {
          this.drawerObserver = new MutationObserver(() => this.scheduleRefresh());
          this.drawerObserver.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
          const accordion = this.closest('[data-accordion-item]');
          if (accordion) this.drawerObserver.observe(accordion, { attributes: true, attributeFilter: ['class'] });
        }
        this.refresh();
      }

      disconnectedCallback() {
        window.cancelAnimationFrame(this.frame);
        this.observer?.disconnect();
        this.drawerObserver?.disconnect();
        this.controller?.abort();
        this.controller = null;
      }

      scheduleRefresh() {
        window.cancelAnimationFrame(this.frame);
        this.frame = window.requestAnimationFrame(() => this.refresh());
      }

      refresh() {
        if (!this.track || !this.isConnected) return;
        this.rtl = window.getComputedStyle(this.track).direction === 'rtl';
        if (this.track.clientWidth > 0) {
          const trackRect = this.track.getBoundingClientRect();
          const images = [...this.track.querySelectorAll('.nosht-header-products__image')];
          // Natural image heights vary: keep arrows on the visible image, above its title.
          const image = images.find(candidate => {
            const rect = candidate.getBoundingClientRect();
            const visibleWidth = Math.min(rect.right, trackRect.right) - Math.max(rect.left, trackRect.left);
            return rect.width > 0 && visibleWidth > rect.width / 2;
          }) || images[0];
          if (image) this.style.setProperty('--nosht-menu-arrow-top', `${image.getBoundingClientRect().height / 2}px`);
        }
        const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        const position = Math.max(0, this.rtl ? -this.track.scrollLeft : this.track.scrollLeft);
        const overflowing = this.track.clientWidth > 0 && maximum > 2;
        const previousUnavailable = !overflowing || position <= 2;
        const nextUnavailable = !overflowing || position >= maximum - 2;
        const focused = document.activeElement;
        const losingFocus = (focused === this.previous && previousUnavailable) || (focused === this.next && nextUnavailable);
        if (losingFocus) {
          // A disabled/hidden native button blurs to BODY. Move focus first so the
          // surrounding menu's focusout handler keeps this deliberate rail interaction open.
          const opposite = focused === this.previous
            ? (!nextUnavailable ? this.next : null)
            : (!previousUnavailable ? this.previous : null);
          if (opposite) {
            opposite.hidden = false;
            opposite.disabled = false;
            opposite.focus({ preventScroll: true });
          } else {
            this.track.querySelector('.nosht-header-products__product')?.focus({ preventScroll: true });
          }
        }
        if (this.previous) {
          this.previous.hidden = previousUnavailable;
          this.previous.disabled = previousUnavailable;
        }
        if (this.next) {
          this.next.hidden = nextUnavailable;
          this.next.disabled = nextUnavailable;
        }
      }

      advance(direction) {
        const first = this.track.querySelector('.nosht-header-products__item');
        if (!first) return;
        const gap = Number.parseFloat(window.getComputedStyle(this.track).columnGap) || 0;
        const count = this.dataset.context === 'drawer' ? 1 : 4;
        const distance = (first.getBoundingClientRect().width + gap) * count;
        this.track.scrollBy({
          left: distance * direction * (this.rtl ? -1 : 1),
          behavior: this.motion.matches ? 'auto' : 'smooth',
        });
      }

      revealFocusedProduct(event) {
        const card = event.target.closest('.nosht-header-products__item');
        if (!card) return;
        const cardRect = card.getBoundingClientRect();
        const trackRect = this.track.getBoundingClientRect();
        const left = cardRect.left < trackRect.left ? cardRect.left - trackRect.left : 0;
        const right = cardRect.right > trackRect.right ? cardRect.right - trackRect.right : 0;
        if (left || right) this.track.scrollBy({ left: left || right, behavior: 'auto' });
      }

      onTrackKeydown(event) {
        const link = event.target.closest('.nosht-header-products__product');
        if (!link || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        const links = Array.from(this.track.querySelectorAll('.nosht-header-products__product'));
        const index = links.indexOf(link);
        let nextIndex;
        if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = links.length - 1;
        else nextIndex = index + ((event.key === 'ArrowRight') !== this.rtl ? 1 : -1);
        if (nextIndex >= 0 && nextIndex < links.length) {
          event.preventDefault();
          links[nextIndex].focus({ preventScroll: true });
        }
      }

      matchesEditorBlock(event) {
        if (!event.detail?.blockId || !this.dataset.menuBlock) return false;
        const selected = String(event.detail.blockId);
        const current = this.dataset.menuBlock;
        return selected === current || selected.split('__').pop() === current.split('__').pop();
      }

      onEditorSelect(event) {
        if (!this.matchesEditorBlock(event)) return;
        const desktopMenu = Array.from(document.querySelectorAll('nosht-product-menu')).find((menu) => {
          return menu.dataset.menuBlock === this.dataset.menuBlock && menu.trigger?.getClientRects().length;
        });
        const showDesktop = this.desktop.matches && desktopMenu;
        if (showDesktop && this.dataset.context === 'desktop') {
          this.editorSelected = true;
          desktopMenu.open();
          desktopMenu.pinned = true;
        } else if (!showDesktop && this.dataset.context === 'drawer' && this.drawer) {
          this.editorSelected = true;
          window.Woolman?.ModalsAndDrawers?.showModalOrDrawer(this.drawer.id);
          this.drawer.ensureSubmenuOpen?.(this.closest('[data-accordion-item]'));
        }
        this.scheduleRefresh();
      }

      onEditorDeselect(event) {
        if (!this.matchesEditorBlock(event) || !this.editorSelected) return;
        this.editorSelected = false;
        const menu = this.closest('nosht-product-menu');
        if (menu) {
          menu.pinned = false;
          menu.close();
        } else if (this.drawer) {
          window.Woolman?.ModalsAndDrawers?.closeModalOrDrawer(this.drawer.id);
        }
      }
    }
    customElements.define('nosht-menu-carousel', NoshtMenuCarousel);
  }
})();
